import { ProfileActions, ProfileMutations, ProfileGetters } from './profile-listing.keys';
import { profileService } from './profile.service';
import { TolErrorsActions } from '@/app/features/tol-errors/tol-errors.keys';
import { TolMessagesActions } from '@/app/features/tol-messages/tol-messages.keys';
import { NotificationsListingActions } from '@/app/features/notifications/features/notifications-listing/notifications-listing.keys';
import i18n from '@/i18n';
import router from '@/router';

export const getInitialState = () => {
  return {
    recentAccessEntries: [],
    recentActionsEntries: [],
    recentViewEntries: [],
    userDetails: {},
    allowedRoles: [],
    isPasswordReset: false
  };
};

export const profilesStore = {
  state: getInitialState(),
  getters: {
    [ProfileGetters.GetRecentAccessEntries]: state => state.recentAccessEntries,
    [ProfileGetters.GetRecentActionsEntries]: state => state.recentActionsEntries,
    [ProfileGetters.GetRecentViewEntries]: state => state.recentViewEntries,
    [ProfileGetters.GetUserDetails]: state => state.userDetails
  },
  mutations: {
    [ProfileMutations.SetRecentAccessEntries](state, accessEntries) {
      state.recentAccessEntries = accessEntries;
    },
    [ProfileMutations.SetRecentActionsEntries](state, actionsEntries) {
      state.recentActionsEntries = actionsEntries;
    },
    [ProfileMutations.SetRecentViewEntries](state, viewEntries) {
      state.recentViewEntries = viewEntries;
    },
    [ProfileMutations.SetCurrentData](state, data) {
      state.userDetails = data;
    },
    [ProfileMutations.SetUserAllowedRoles]: (state, roles) => {
      state.allowedRoles = roles;
    },
    [ProfileMutations.SetUserActiveInactive]: (state, activeInactive) => {
      state.userDetails.is_active = activeInactive;
    },
    [ProfileMutations.IsPasswordReset]: (state, flag = true) => {
      state.isPasswordReset = flag;
    }
  },
  actions: {
    [ProfileActions.UpdateRecentAccessEntries]({ commit }, userId) {
      profileService.getUserLogins(userId).then(accessEntries => {
        commit(ProfileMutations.SetRecentAccessEntries, accessEntries);
      });
    },
    [ProfileActions.UpdateRecentActionsEntries]({ commit }, userId) {
      profileService.getUserActions(userId).then(accessActions => {
        commit(ProfileMutations.SetRecentActionsEntries, accessActions);
      });
    },
    [ProfileActions.UpdateRecentViewEntries]({ commit }, userId) {
      profileService.getUserViews(userId).then(accessViews => {
        commit(ProfileMutations.SetRecentViewEntries, accessViews);
      });
    },
    [ProfileActions.UpdateUserData]({ commit }, userReferenceNumber) {
      profileService.fetchUserDetails(userReferenceNumber).then(userData => {
        userData.firstName = userData.first_name;
        userData.lastName = userData.last_name;

        commit(ProfileMutations.SetCurrentData, userData);
      });
    },
    [ProfileActions.SaveUserDetails]({ commit, dispatch }, userDetails) {
      let { currentUserId, username, userRoles, id, isActive } = userDetails;
      profileService
        .saveUserDetails(username, userRoles, id, isActive)
        .then(status => {
          dispatch(TolErrorsActions.ClearAll);
          if (status && status.message == 'success') {
            dispatch(TolMessagesActions.SetMessages, status.info);
            // Set the updated user in the state if it is the current user
            if (status.data && currentUserId === status.data.id) {
              status.data.firstName = status.data.first_name;
              status.data.lastName = status.data.last_name;
              commit(ProfileMutations.SetCurrentData, status.data);
            }
          } else if (!status.errors) {
            // Display generic error message if not provided by the API
            dispatch(TolErrorsActions.SetError, { message: i18n.t('profile.errors.updateError') });
          } else {
            dispatch(TolMessagesActions.ClearAll);
            const messages = Object.values(status.errors);
            let items = [];
            for (let item of messages) {
              items = items.concat(item);
            }
            dispatch(TolErrorsActions.SetError, { items });
          }
        })
        .catch(() => {
          // Display generic error message if not provided by the API
          dispatch(TolErrorsActions.SetError, { message: i18n.t('profile.errors.updateError') });
        });
    },
    // eslint-disable-next-line no-unused-vars
    [ProfileActions.CheckIfUsernameIsTaken]: async ({ dispatch }, username) => {
      try {
        const response = await profileService.checkIfUsernameIsTaken(username);
        return response.data;
      } catch (error) {
        console.error(error);
      }
    },
    [ProfileActions.FetchUserAllowedRoles]: ({ commit }, userId) => {
      return profileService
        .fetchUserAllowedRoles(userId)
        .then(result => {
          commit(ProfileMutations.SetUserAllowedRoles, result);
        })
        .catch(err => {
          console.log(err);
        });
    },
    [ProfileActions.ImpersonateUser]: ({ dispatch }, userName) => {
      return profileService.impersonateUser(userName).then(res => {
        if (typeof res === 'string') {
          localStorage.setItem('tol-token', res);
          /**
           * TODO call resetStore so that state of admin user is reset before redirecting to
           * the users's page
           *
           * Reset state needs to be refactored. Current code for resetting state is ancient
           * and does not reflect the changes that have been made to the application
           * TOL4-698
           */
          dispatch(NotificationsListingActions.RetrieveNotifications);
          router.replace({ name: 'home' });
        } else {
          dispatch(TolMessagesActions.SetMessages, { text: res.message });
        }
      });
    },
    [ProfileActions.ToggleUserActive]: ({ commit }, activeInactive) => {
      commit(ProfileMutations.SetUserActiveInactive, activeInactive);
    },
    [ProfileActions.SendChangeInstructions]: async ({ commit }, props) => {
      const res = await profileService.sendChangeInstructions(props.username, props.email);
      commit(ProfileMutations.IsPasswordReset);
      return res;
    }
  }
};
